// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { getLogger } from '@blueberry/observability'
import { trackAfterDuration } from '../../../lib/meta_timer_based_tracking'
import { SubmitD2cResponse } from '../controllers/checkout_controller'
import { AccountForm } from '../controllers/form_account_controller'
import { AddressForm } from '../controllers/form_address_controller'
import { PlansForm } from '../controllers/form_plans_controller'
import { getCookieByName } from './cookie_utils'

type SignupEventType =
  | 'Signup Page #page1'
  | 'Signup Page #page2'
  | 'Signup Page #page3'
  | 'Signup Success'
  | 'Signup Insurance Contact Page'
  | 'Signup Insurance Child Eligibility Page'
  | 'Signup Insurance Parent Eligibility Page'
  | 'Signup Insurance Address Page'
  | 'Signup Insurance'
  | 'Insurance Signup Success'
  | 'Mobile CTA Initial Login'

const logger = getLogger('track_event')

export async function trackEvent(
  action: SignupEventType,
  additionalData: Record<string, unknown>,
) {
  try {
    additionalData['_fbp'] = getCookieByName('_fbp')
    additionalData['_fbc'] = getCookieByName('_fbc')

    const response = await fetch('/track_event', {
      method: 'POST',
      body: JSON.stringify({
        action,
        additional_data: JSON.stringify(additionalData),
        url: window.location.href + window.queryParams?.toString(),
        event_level: 'INFO',
      }),
    })

    if (!response.ok) {
      logger.error(
        'Error tracking event: Unexpected response code',
        response.status,
        response.statusText,
      )
      return
    }
  } catch (error) {
    logger.error('Error tracking event:', error)
  }
}

async function waitForOptibaseInitialization() {
  const MAX_RETRIES = 50
  const RETRY_DELAY = 100

  for (let retries = 0; retries < MAX_RETRIES; retries++) {
    if (window.optibaseInitialized) {
      return true
    }
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY))
  }

  return false
}

async function optibaseSendConversionEvent(userFriendlyConversionId: string) {
  const isOptibaseInitialized = await waitForOptibaseInitialization()

  if (!isOptibaseInitialized) {
    logger.error('Error: Retries exhausted waiting for Optibase to initialize')
    return
  }

  try {
    window.optibaseSendConversionEvent(userFriendlyConversionId)
  } catch (error) {
    logger.error('Error tracking Optibase event:', error)
  }
}

type NorthbeamPurchaseEvent = {
  id: string
  totalPrice: number
  shippingPrice: number
  taxPrice: number
  currency: 'USD'
  customerId: string
  lineItems: {
    productId: string
    variantId: string
    productName: string
    variantName: string
    price: number
    quantity: number
  }[]
}

type PartneroCustomerSignup = (
  resource: 'customers',
  action: 'signup',
  data: {
    data: {
      key: string // customer email
      name: string
      email: string
    }
  },
) => void

type PartneroTransactionCreate = (
  resource: 'transactions',
  action: 'create',
  data: {
    data: {
      key: string
      amount: number
      amount_units: 'usd'
      customer: {
        key: string // customer email
      }
    }
  },
) => void

// analytics tools are not typed, so manually type them here
declare global {
  interface Window {
    // northbeam
    Northbeam: {
      identify: (key: string, value: string) => void
      trackPageView: (pageName: string) => void
      firePurchaseEvent: (event: NorthbeamPurchaseEvent) => void
    }

    // gtag
    gtag: (command: string, event: string, details?: unknown) => void

    // partnero
    po: PartneroCustomerSignup | PartneroTransactionCreate

    // facebook pixel
    fbq: (
      command: 'track' | 'init' | 'trackCustom',
      event: string,
      data?: unknown,
      options?: { eventID: string },
    ) => void

    // optibase
    optibaseSendConversionEvent: (userFriendlyConversionId: string) => void
    optibaseInitialized: boolean

    // microsoft/bing uet
    uetq: unknown[]
  }
}

export function trackPage1() {
  window.gtag('event', 'checkout_progress', {
    event_label: 'Signup Page One',
    event_category: 'signup',
  })

  window.gtag('event', 'signup_page_1')
  window.gtag('event', 'conversion', {
    send_to: 'AW-816975002/XGIACPGc9n4QmpnIhQM',
  })

  const uniqueID = `evt-${crypto.randomUUID()}`

  trackEvent('Signup Page #page1', {
    full_url: window.location.href,
    eventID: uniqueID,
    ...getQueryParams(),
  })

  trackAfterDuration('Signup Page #page1', uniqueID)

  optibaseSendConversionEvent('signup_page_1')

  void window.Northbeam.trackPageView('Signup Page One')
}

export function trackPartnerPage1(partnerId: string) {
  window.gtag('event', 'checkout_progress', {
    event_label: 'Signup Insurance Contact Page',
    event_category: 'signup',
  })

  optibaseSendConversionEvent('signup_page_1')

  trackEvent('Signup Insurance Contact Page', {
    full_url: window.location.href,
    payer_partner_id: partnerId,
    ...getQueryParams(),
  })
}

export async function trackPage2(accountForm: AccountForm) {
  // We must set user_data before sending the gtag event to ensure that user is
  // associated with conversion events
  window.gtag('set', 'user_data', {
    email: accountForm.email,
  })

  window.gtag('event', 'checkout_progress', {
    event_label: 'Signup Page Two',
    event_category: 'signup',
  })

  window.gtag('event', 'signup_page_2')
  window.gtag('event', 'conversion', {
    send_to: 'AW-816975002/ldlKCMjk24ABEJqZyIUD',
  })
  const uniqueID = `evt-${crypto.randomUUID()}`

  trackEvent('Signup Page #page2', {
    full_url: window.location.href,
    first_name: accountForm.first_name,
    last_name: accountForm.last_name,
    email: accountForm.email,
    eventID: uniqueID,
    ...getQueryParams(),
  })

  trackAfterDuration('Signup Page #page2', uniqueID)

  window.Northbeam.trackPageView('Signup Page Two')
  window.Northbeam.identify('email', accountForm.email)

  optibaseSendConversionEvent('signup_page_2')

  void (window.po as PartneroCustomerSignup)('customers', 'signup', {
    data: {
      key: accountForm.email,
      email: accountForm.email,
      name: `${accountForm.first_name} ${accountForm.last_name}`,
    },
  })
}

export async function trackPartnerPage2(
  partnerId: string,
  eligibilityType: 'child' | 'parent',
  accountForm: AccountForm,
) {
  window.gtag('set', 'user_data', {
    email: accountForm.email,
  })

  optibaseSendConversionEvent('signup_page_2')

  const action =
    eligibilityType === 'child'
      ? 'Signup Insurance Child Eligibility Page'
      : 'Signup Insurance Parent Eligibility Page'

  trackEvent(action, {
    full_url: window.location.href,
    payer_partner_id: partnerId,
    first_name: accountForm.first_name,
    last_name: accountForm.last_name,
    email: accountForm.email,
    ...getQueryParams(),
  })
}

export function trackPage3(accountForm: AccountForm, addressForm: AddressForm) {
  window.gtag('event', 'checkout_progress', {
    event_label: 'Signup Page Three',
    event_category: 'signup',
  })

  window.gtag('event', 'signup_page_3')
  window.gtag('event', 'conversion', {
    send_to: 'AW-816975002/kTTGCOTeh6sZEJqZyIUD',
  })

  const uniqueID = `evt-${crypto.randomUUID()}`

  trackEvent('Signup Page #page3', {
    full_url: window.location.href,
    first_name: accountForm.first_name,
    last_name: accountForm.last_name,
    email: accountForm.email,
    phone: addressForm.phone,
    state: addressForm.state,
    zipcode: addressForm.zip,
    city: addressForm.city,
    eventID: uniqueID,
    ...getQueryParams(),
  })

  trackAfterDuration('Signup Page #page3', uniqueID)

  optibaseSendConversionEvent('signup_page_3')

  void window.Northbeam.trackPageView('Signup Page Three')
}

export function trackPartnerPage3(partnerId: string, accountForm: AccountForm) {
  optibaseSendConversionEvent('signup_page_3')

  trackEvent('Signup Insurance Address Page', {
    full_url: window.location.href,
    payer_partner_id: partnerId,
    first_name: accountForm.first_name,
    last_name: accountForm.last_name,
    email: accountForm.email,
    ...getQueryParams(),
  })
}

type TrackSuccessParams = {
  submitResponse: SubmitD2cResponse
  accountForm: AccountForm
  addressForm: AddressForm
  plansForm: PlansForm
}

export async function trackSuccess({
  submitResponse,
  accountForm,
  addressForm,
  plansForm,
}: TrackSuccessParams) {
  const uniqueID = `evt-${crypto.randomUUID()}`

  // custom event to replace 'Purchase', 'AddToCart', and 'Lead' non-custom events once core setup restriction is enforced on 2/14/2025
  // https://www.facebook.com/business/help/124742407297678
  window.fbq(
    'trackCustom',
    'Signup Success',
    {
      value: submitResponse.totalPrice,
      currency: 'USD',
    },
    { eventID: uniqueID },
  )

  trackEvent('Signup Success', {
    full_url: window.location.href,
    first_name: accountForm.first_name,
    last_name: accountForm.last_name,
    email: accountForm.email,
    phone: addressForm.phone,
    state: addressForm.state,
    zipcode: addressForm.zip,
    city: addressForm.city,
    plan: plansForm.plan,
    eventID: uniqueID,
    value: submitResponse.totalPrice,
    ...getQueryParams(),
  })

  optibaseSendConversionEvent('signup_success')

  window.Northbeam.firePurchaseEvent({
    id: submitResponse.hashedSignupData + '_signup', // needs to match string in backend
    totalPrice: submitResponse.totalPrice,
    shippingPrice: 0,
    taxPrice: 0,
    currency: 'USD',
    customerId: submitResponse.hashedProfileId + '_customer', // needs to match string in backend
    lineItems: [
      {
        productId: plansForm.plan === 'monthly' ? 'monthly_sku' : 'annual_sku',
        variantId: plansForm.plan === 'monthly' ? 'monthly' : 'annual',
        productName: 'Signup',
        variantName: 'Small',
        price: submitResponse.totalPrice,
        quantity: 1,
      },
    ],
  })

  void (window.po as PartneroTransactionCreate)('transactions', 'create', {
    data: {
      key: uniqueID,
      amount: submitResponse.totalPrice,
      amount_units: 'usd',
      customer: {
        key: accountForm.email,
      },
    },
  })

  window.uetq = window.uetq || []
  window.uetq.push('event', 'purchase', {})

  await Promise.all([
    new Promise((resolve) => {
      window.gtag('event', 'checkout_progress', {
        event_label: 'Signup Success',
        event_category: 'signup',
        event_callback: () => resolve(true),
      })
    }),
    new Promise((resolve) => {
      window.gtag('event', 'conversion', {
        send_to: 'AW-816975002/4__hCKXQ-X4QmpnIhQM',
        event_callback: () => resolve(true),
      })
    }),
  ])
}

type TrackPartnerSuccessParams = {
  partnerId: string
  accountForm: AccountForm
  addressForm: AddressForm
}

export function trackPartnerSuccess({
  partnerId,
  accountForm,
  addressForm,
}: TrackPartnerSuccessParams) {
  window.gtag('event', 'checkout_progress', {
    event_label: 'Insurance Signup Success',
    event_category: 'signup',
  })

  void trackEvent('Insurance Signup Success', {
    full_url: window.location.href,
    payer_partner_id: partnerId,
    first_name: accountForm.first_name,
    last_name: accountForm.last_name,
    email: accountForm.email,
    phone: addressForm.phone,
    state: addressForm.state,
    ...getQueryParams(),
  })
}

export function getQueryParams() {
  const searchParams = new URLSearchParams(window.location.search)
  return Object.fromEntries(searchParams.entries())
}
